import { trackEvent } from "@excalidraw/excalidraw/analytics";
import { copyTextToSystemClipboard } from "@excalidraw/excalidraw/clipboard";
import { Dialog } from "@excalidraw/excalidraw/components/Dialog";
import { FilledButton } from "@excalidraw/excalidraw/components/FilledButton";
import { TextField } from "@excalidraw/excalidraw/components/TextField";
import {
  copyIcon,
  LinkIcon,
  playerPlayIcon,
  playerStopFilledIcon,
  share,
  shareIOS,
  shareWindows,
} from "@excalidraw/excalidraw/components/icons";
import { useUIAppState } from "@excalidraw/excalidraw/context/ui-appState";
import { useCopyStatus } from "@excalidraw/excalidraw/hooks/useCopiedIndicator";
import { useI18n } from "@excalidraw/excalidraw/i18n";
import { KEYS, getFrame } from "@excalidraw/common";
import { useEffect, useRef, useState } from "react";

import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";

import { atom, useAtom, useAtomValue } from "../app-jotai";
import { activeRoomLinkAtom } from "../collab/Collab";
import { libraryApi } from "../data/libraryApi";

import "./ShareDialog.scss";
import { QRCode } from "./QRCode";

import type { CollabAPI } from "../collab/Collab";

type ShareDialogType = "share" | "collaborationOnly";

export const shareDialogStateAtom = atom<
  { isOpen: false } | { isOpen: true; type: ShareDialogType }
>({ isOpen: false });

const getShareIcon = () => {
  const navigator = window.navigator as any;
  const isAppleBrowser = /Apple/.test(navigator.vendor);
  const isWindowsBrowser = navigator.appVersion.indexOf("Win") !== -1;

  if (isAppleBrowser) {
    return shareIOS;
  } else if (isWindowsBrowser) {
    return shareWindows;
  }

  return share;
};

export type ShareDialogProps = {
  collabAPI: CollabAPI | null;
  handleClose: () => void;
  excalidrawAPI: ExcalidrawImperativeAPI | null;
  activeCanvasId: string | null;
  type: ShareDialogType;
};

const ActiveRoomDialog = ({
  collabAPI,
  activeRoomLink,
  handleClose,
}: {
  collabAPI: CollabAPI;
  activeRoomLink: string;
  handleClose: () => void;
}) => {
  const { t } = useI18n();
  const [, setJustCopied] = useState(false);
  const timerRef = useRef<number>(0);
  const ref = useRef<HTMLInputElement>(null);
  const isShareSupported = "share" in navigator;
  const { onCopy, copyStatus } = useCopyStatus();

  const copyRoomLink = async () => {
    try {
      await copyTextToSystemClipboard(activeRoomLink);
    } catch (e) {
      collabAPI.setCollabError(t("errors.copyToSystemClipboardFailed"));
    }

    setJustCopied(true);

    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
    }

    timerRef.current = window.setTimeout(() => {
      setJustCopied(false);
    }, 3000);

    ref.current?.select();
  };

  const shareRoomLink = async () => {
    try {
      await navigator.share({
        title: t("roomDialog.shareTitle"),
        text: t("roomDialog.shareTitle"),
        url: activeRoomLink,
      });
    } catch (error: any) {
      // Just ignore.
    }
  };

  return (
    <>
      <h3 className="ShareDialog__active__header">
        {t("labels.liveCollaboration").replace(/\./g, "")}
      </h3>
      <TextField
        defaultValue={collabAPI.getUsername()}
        placeholder="Your name"
        label="Your name"
        onChange={collabAPI.setUsername}
        onKeyDown={(event) => event.key === KEYS.ENTER && handleClose()}
      />
      <div className="ShareDialog__active__linkRow">
        <TextField
          ref={ref}
          label="Link"
          readonly
          fullWidth
          value={activeRoomLink}
        />
        {isShareSupported && (
          <FilledButton
            size="large"
            variant="icon"
            label="Share"
            icon={getShareIcon()}
            className="ShareDialog__active__share"
            onClick={shareRoomLink}
          />
        )}
        <FilledButton
          size="large"
          label={t("buttons.copyLink")}
          icon={copyIcon}
          status={copyStatus}
          onClick={() => {
            copyRoomLink();
            onCopy();
          }}
        />
      </div>
      <QRCode value={activeRoomLink} />
      <div className="ShareDialog__active__description">
        <p>
          <span
            role="img"
            aria-hidden="true"
            className="ShareDialog__active__description__emoji"
          >
            🔒{" "}
          </span>
          {t("roomDialog.desc_privacy")}
        </p>
        <p>{t("roomDialog.desc_exitSession")}</p>
      </div>

      <div className="ShareDialog__active__actions">
        <FilledButton
          size="large"
          variant="outlined"
          color="danger"
          label={t("roomDialog.button_stopSession")}
          icon={playerStopFilledIcon}
          onClick={() => {
            trackEvent("share", "room closed");
            collabAPI.stopCollaboration();
            if (!collabAPI.isCollaborating()) {
              handleClose();
            }
          }}
        />
      </div>
    </>
  );
};

const SelfHostedShareSection = ({
  excalidrawAPI,
  activeCanvasId,
}: {
  excalidrawAPI: ExcalidrawImperativeAPI | null;
  activeCanvasId: string | null;
}) => {
  const { t } = useI18n();
  const { onCopy, copyStatus } = useCopyStatus();
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [description, setDescription] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Pre-fill the existing, permanent link for this canvas (if any) so the same
  // URL shows up every time instead of minting a new one.
  useEffect(() => {
    let cancelled = false;
    if (!activeCanvasId) {
      return;
    }
    libraryApi
      .getShareForCanvas(activeCanvasId)
      .then((res) => {
        if (!cancelled) {
          setShareUrl(`${window.location.origin}/share/${res.token}`);
        }
      })
      .catch(() => {
        /* no share yet — leave the create button */
      });
    return () => {
      cancelled = true;
    };
  }, [activeCanvasId]);

  // Load the canvas description so the textarea reflects what's stored.
  useEffect(() => {
    let cancelled = false;
    if (!activeCanvasId) {
      setDescription("");
      return;
    }
    libraryApi
      .getCanvas(activeCanvasId)
      .then((meta) => {
        if (!cancelled) {
          setDescription(meta.description ?? "");
        }
      })
      .catch(() => {
        /* ignore */
      });
    return () => {
      cancelled = true;
    };
  }, [activeCanvasId]);

  // Persist the description to the canvas, debounced while the user types.
  const onDescriptionChange = (value: string) => {
    setDescription(value);
    if (!activeCanvasId) {
      return;
    }
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
    }
    saveTimer.current = setTimeout(() => {
      libraryApi
        .updateCanvas(activeCanvasId, { description: value || null })
        .catch(() => {
          /* ignore — will retry on next edit */
        });
    }, 700);
  };

  const createLink = async () => {
    if (!excalidrawAPI || busy) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const appState = excalidrawAPI.getAppState();
      const scene = {
        type: "excalidraw",
        version: 2,
        source: "self-hosted",
        elements: excalidrawAPI.getSceneElements(),
        appState: { viewBackgroundColor: appState.viewBackgroundColor },
        files: excalidrawAPI.getFiles(),
      };
      let name: string | null = null;
      if (activeCanvasId) {
        try {
          const meta = await libraryApi.getCanvas(activeCanvasId);
          name = meta.name;
        } catch {
          /* ignore — share without a name */
        }
      }
      // Flush any pending debounced description save so the snapshot is fresh.
      if (saveTimer.current) {
        clearTimeout(saveTimer.current);
        saveTimer.current = null;
      }
      const res = await libraryApi.createShare({
        scene,
        name,
        canvas_id: activeCanvasId,
        description: description || null,
      });
      const url = `${window.location.origin}/share/${res.token}`;
      setShareUrl(url);
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 0);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    if (!shareUrl) {
      return;
    }
    try {
      await copyTextToSystemClipboard(shareUrl);
      onCopy();
    } catch {
      // insecure context — the input is selected so the user can copy manually
    }
  };

  return (
    <>
      <div className="ShareDialog__picker__header">
        {t("shareLink.dialogTitle")}
      </div>
      <div className="ShareDialog__picker__description">
        {t("shareLink.hint")}
      </div>

      <label className="ShareDialog__descriptionField">
        <span className="ShareDialog__descriptionField__label">
          {t("shareLink.descriptionLabel")}
        </span>
        <textarea
          className="ShareDialog__descriptionField__input"
          rows={2}
          maxLength={300}
          placeholder={t("shareLink.descriptionPlaceholder")}
          value={description}
          onChange={(e) => onDescriptionChange(e.target.value)}
        />
      </label>

      {shareUrl ? (
        <div className="ShareDialog__active__linkRow">
          <TextField
            ref={inputRef}
            label="Link"
            readonly
            fullWidth
            value={shareUrl}
          />
          <FilledButton
            size="large"
            label={t("shareLink.copy")}
            icon={copyIcon}
            status={copyStatus}
            onClick={copyLink}
          />
        </div>
      ) : (
        <div className="ShareDialog__picker__button">
          <FilledButton
            size="large"
            label={busy ? "…" : t("shareLink.createLink")}
            icon={LinkIcon}
            onClick={createLink}
          />
        </div>
      )}

      {error && (
        <div className="ShareDialog__picker__description ShareDialog__error">
          {error}
        </div>
      )}
    </>
  );
};

const ShareDialogPicker = (props: ShareDialogProps) => {
  const { t } = useI18n();

  const { collabAPI } = props;

  const startCollabJSX = collabAPI ? (
    <>
      <div className="ShareDialog__picker__header">
        {t("labels.liveCollaboration").replace(/\./g, "")}
      </div>

      <div className="ShareDialog__picker__description">
        <div style={{ marginBottom: "1em" }}>{t("roomDialog.desc_intro")}</div>
        {t("roomDialog.desc_privacy")}
      </div>

      <div className="ShareDialog__picker__button">
        <FilledButton
          size="large"
          label={t("roomDialog.button_startSession")}
          icon={playerPlayIcon}
          onClick={() => {
            trackEvent("share", "room creation", `ui (${getFrame()})`);
            collabAPI.startCollaboration(null);
          }}
        />
      </div>

      {props.type === "share" && (
        <div className="ShareDialog__separator">
          <span>{t("shareDialog.or")}</span>
        </div>
      )}
    </>
  ) : null;

  return (
    <>
      {startCollabJSX}

      {props.type === "share" && (
        <SelfHostedShareSection
          excalidrawAPI={props.excalidrawAPI}
          activeCanvasId={props.activeCanvasId}
        />
      )}
    </>
  );
};

const ShareDialogInner = (props: ShareDialogProps) => {
  const activeRoomLink = useAtomValue(activeRoomLinkAtom);

  return (
    <Dialog size="small" onCloseRequest={props.handleClose} title={false}>
      <div className="ShareDialog">
        {props.collabAPI && activeRoomLink ? (
          <ActiveRoomDialog
            collabAPI={props.collabAPI}
            activeRoomLink={activeRoomLink}
            handleClose={props.handleClose}
          />
        ) : (
          <ShareDialogPicker {...props} />
        )}
      </div>
    </Dialog>
  );
};

export const ShareDialog = (props: {
  collabAPI: CollabAPI | null;
  excalidrawAPI: ExcalidrawImperativeAPI | null;
  activeCanvasId: string | null;
}) => {
  const [shareDialogState, setShareDialogState] = useAtom(shareDialogStateAtom);

  const { openDialog } = useUIAppState();

  useEffect(() => {
    if (openDialog) {
      setShareDialogState({ isOpen: false });
    }
  }, [openDialog, setShareDialogState]);

  if (!shareDialogState.isOpen) {
    return null;
  }

  return (
    <ShareDialogInner
      handleClose={() => setShareDialogState({ isOpen: false })}
      collabAPI={props.collabAPI}
      excalidrawAPI={props.excalidrawAPI}
      activeCanvasId={props.activeCanvasId}
      type={shareDialogState.type}
    />
  );
};
